import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { Activity, Smartphone, Monitor, Tablet, RefreshCw, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScanRecord, getCountryFlag, useRealtimeScans } from '@/hooks/useQRAnalytics';
import { toast } from 'sonner';

interface ScanActivityFeedProps {
  scans: ScanRecord[];
  qrCodeId: string | null;
  onRefresh: () => void;
}

const getDeviceIcon = (deviceType: string | null) => {
  const device = deviceType?.toLowerCase() || '';
  if (device.includes('mobile')) return Smartphone;
  if (device.includes('tablet')) return Tablet;
  return Monitor;
};

export function ScanActivityFeed({ scans, qrCodeId, onRefresh }: ScanActivityFeedProps) {
  const [newScans, setNewScans] = useState<ScanRecord[]>([]);
  const recentScans = [...newScans, ...scans].slice(0, 50);

  // Real-time subscription
  useRealtimeScans(qrCodeId, (newScan) => {
    setNewScans(prev => [newScan, ...prev]);
    toast.info(
      `New scan from ${newScan.city || 'Unknown'}, ${newScan.country || 'Unknown'}`,
      {
        icon: <Bell className="h-4 w-4" />,
        duration: 3000,
      }
    );
  });

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      onRefresh();
    }, 30000);
    return () => clearInterval(interval);
  }, [onRefresh]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
      className="surface-elevated rounded-xl p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Recent Scans</h3>
          <Badge variant="outline" className="text-xs">Live</Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {recentScans.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No scans yet</p>
          <p className="text-xs mt-1">Share your QR code to start tracking</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
          <AnimatePresence initial={false}>
            {recentScans.map((scan, index) => {
              const DeviceIcon = getDeviceIcon(scan.device_type);
              const isNew = newScans.some(ns => ns.id === scan.id);
              
              return (
                <motion.div
                  key={scan.id}
                  initial={isNew ? { opacity: 0, x: -20, scale: 0.95 } : false}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg 
                    ${isNew ? 'bg-primary/10 border border-primary/20' : 'bg-muted/50'}
                    transition-colors
                  `}
                >
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <DeviceIcon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">
                        {scan.city || 'Unknown'}, {scan.country || 'Unknown'}
                      </span>
                      <span className="text-base shrink-0">
                        {getCountryFlag(scan.country || '')}
                      </span>
                      {isNew && (
                        <Badge className="text-[10px] px-1.5 py-0 bg-primary/20 text-primary">
                          New
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{scan.os || 'Unknown OS'}</span>
                      <span>•</span>
                      <span>{scan.browser || 'Unknown Browser'}</span>
                    </div>
                  </div>
                  
                  <div className="text-xs text-muted-foreground shrink-0">
                    {formatDistanceToNow(new Date(scan.scanned_at), { addSuffix: true })}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
}
